import { TableColumnService } from '../services/table-column.service';
import { combineLatest, fromEvent, Observable, of, timer } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { Sort, SortDirection } from '@angular/material/sort';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { TableDataQuery } from '../../../models/table-data-query';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { TableColumn } from '../../../models/table-column';
import { EntityEnum } from '../../../enum/entity.enum';
import { Injectable, Signal } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export abstract class TableService<E, Q> {

    public readonly busy$: Observable<boolean>;
    public readonly selection$: Observable<string[]>;
    public readonly locked$: Observable<string[]>;
    public readonly totalCount$: Observable<number>;
    public readonly selectionAny$: Observable<boolean>;
    public readonly selectionIndeterminate$: Observable<boolean>;
    public readonly sortColumn$: Observable<string>;
    public readonly sortDirection$: Observable<SortDirection>;
    public readonly currentPageNumber$: Observable<number>;
    public readonly columnDefinitions: TableColumn[] = [];
    public readonly displayColumns$: Observable<string[]>;
    public readonly currentPageEntries$: Observable<E[]>;
    public readonly empty$: Observable<boolean>;
    public readonly selection: Signal<string[]>;

    public tableHeight$: Observable<string> = combineLatest([
        timer(0, 1000),
        fromEvent(window, 'resize').pipe(startWith(0))
    ]).pipe(
        map(() => {
            const filterRect = document.getElementsByClassName('table-filter')[0].getBoundingClientRect();
            const footerHeight = document.getElementsByClassName('table-footer')[0].clientHeight;
            const scrollerHeight = window.innerHeight - (filterRect.bottom + footerHeight);
            return scrollerHeight + 'px';
        }),
        distinctUntilChanged()
    );

    protected constructor(private tableColumnService: TableColumnService, private breakpoints: BreakpointObserver,
                          protected store: Store<AppState>) {
        this.busy$ = this.getBusyObservable();
        this.locked$ = this.getLockedObservable();
        this.totalCount$ = this.getTotalCountObservable();
        this.selectionAny$ = this.getSelectionObservable().pipe(
            map(selection => selection.length > 0)
        );
        this.sortColumn$ = this.getTableQueryObservable().pipe(
            map((q: TableDataQuery) => q.sortColumn)
        );
        this.sortDirection$ = this.getTableQueryObservable().pipe(
            map((q: TableDataQuery) => q.sortDescending ? 'desc' : 'asc')
        );
        this.currentPageNumber$ = this.getTableQueryObservable().pipe(
            map((q: TableDataQuery) => q.pageNumber - 1)
        );
        this.selectionIndeterminate$ = combineLatest([ this.getSelectionObservable(), this.getCurrentPageEntriesObservable() ]).pipe(
            map(([ selection, page ]) => selection.length > 0 && selection.length < page.length)
        );
        this.selection$ = this.getSelectionObservable();
        this.selection = toSignal(this.selection$);
        this.columnDefinitions = this.tableColumnService.getColumnsForEntity(this.getEntity());
        this.displayColumns$ = this.getColumnSelectionObservable();
        this.currentPageEntries$ = this.getCurrentPageEntriesObservable();
        this.empty$ = combineLatest([ this.busy$, this.totalCount$ ]).pipe(
            map(([ busy, totalCount ]) => !busy && totalCount === 0)
        );
    }

    public hidePageSize$: Observable<boolean> = this.breakpoints.observe([ Breakpoints.XSmall ]).pipe(
        map(() => this.breakpoints.isMatched(Breakpoints.XSmall))
    );
    public showFirstLast$: Observable<boolean> = this.hidePageSize$.pipe(
        map(hide => !hide)
    );
    public minRowWidth$: Observable<string> = this.getColumnSelectionObservable().pipe(
        map(list => list
            .filter(c => this.tableColumnService.has(c))
            .map(c => this.tableColumnService.get(c).width)
            .reduce((a, n) => a + n) + this.getWidthBufferPx() + 'px'
        )
    );

    public renderCell(column: TableColumn, entity: any): string {
        if (column.render) {
            return column.render(entity);
        }
        return '';
    }

    public cellClasses(column: TableColumn, entity: any): string {
        if (column.classes) {
            return column.classes(entity);
        }
        return '';
    }

    protected getWidthBufferPx(): number {
        return 200;
    }

    protected getLockedObservable(): Observable<string[]>
    {
        return of([]);
    }

    protected abstract getBusyObservable(): Observable<boolean>;

    protected abstract getTotalCountObservable(): Observable<number>;

    protected abstract getCurrentPageEntriesObservable(): Observable<E[]>;

    protected abstract getSelectionObservable(): Observable<string[]>;

    protected abstract getColumnSelectionObservable(): Observable<string[]>;

    protected abstract getTableQueryObservable(): Observable<Q>;

    protected abstract getEntity(): EntityEnum;

    public abstract toggle(id: string): void;

    public abstract toggleAll(): void;

    public abstract pageChange(page: PageEvent): void;

    public abstract sortChange(sort: Sort): void;

}
